import React from 'react';
import Card from '../../components/ui/Card.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Button from '../../components/ui/Button.jsx';
import { apiFetch } from '../../api.js';
import { useApi } from '../../hooks/useApi.js';
import { RouteContext } from '../../router.jsx';

export default function StaffRequestDetail({ params }) {
  const { navigate } = React.useContext(RouteContext);
  const requestId = params?.id;
  const [refresh, setRefresh] = React.useState(0);
  const { data: me } = useApi('/api/me');
  const { data, loading, error } = useApi(`/api/requests/${requestId}`, [requestId, refresh]);
  const [notes, setNotes] = React.useState('');
  const [message, setMessage] = React.useState({ text: '', tone: 'info' });
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setMessage({ text: '', tone: 'info' });
  }, [requestId]);

  if (!requestId) {
    return (
      <div className="page">
        <div className="alert error">Missing request id.</div>
      </div>
    );
  }

  const isChair = me?.user?.chair_privilege === 1;
  const canReview = !!me?.user && (me.user.admin_privilege === 1 || me.user.chair_privilege === 1 || me.user.edit_privilege === 1);
  const canAdvisorAct = data && data.status === 1 && data.advisor_decision === 0 && data.chair_decision === 0;
  const canChairAct = data && data.status === 1 && data.chair_decision === 0;
  const canAct = canReview && (isChair ? canChairAct : canAdvisorAct);

  const handleDecision = async (decision, chair) => {
    setSaving(true);
    setMessage({ text: '', tone: 'info' });
    try {
      await apiFetch(`/api/requests/${requestId}/${chair ? 'chair' : 'advisor'}`, {
        method: 'POST',
        body: JSON.stringify({
          decision,
          notes
        })
      });
      setNotes('');
      setRefresh((value) => value + 1);
      setMessage({ text: 'Decision saved.', tone: 'info' });
    } catch (err) {
      setMessage({ text: err?.message || 'Failed to update request.', tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Request Details</h1>
          <p className="subtle">Review and record advisor or chair decisions.</p>
        </div>
        <Button variant="secondary" type="button" onClick={() => navigate('/staff/requests')}>Back to Requests</Button>
      </div>

      {error ? <div className="alert error">{error}</div> : null}
      {message.text ? <div className={message.tone === 'error' ? 'alert error' : 'alert'}>{message.text}</div> : null}

      {loading || !data ? (
        <p className="subtle">Loading...</p>
      ) : (
        <div className="grid two">
          <Card title="Request Summary">
            <div className="summary-list">
              <div className="summary-row">
                <span>Request ID</span>
                <span>REQ-{data.id}</span>
              </div>
              <div className="summary-row">
                <span>Student</span>
                <span>{data.student || data.cin}</span>
              </div>
              <div className="summary-row">
                <span>CIN</span>
                <span>{data.cin}</span>
              </div>
              <div className="summary-row">
                <span>Submitted</span>
                <span>{data.submit_date || '—'}</span>
              </div>
              <div className="summary-row">
                <span>Category</span>
                <span>{data.category || '—'}</span>
              </div>
              <div className="summary-row">
                <span>Change</span>
                <span>{data.remove_course || '—'} → {data.add_course || '—'}</span>
              </div>
              <div className="summary-row">
                <span>Status</span>
                <span>{renderStatus(data)}</span>
              </div>
            </div>
          </Card>

          <Card title="Advisor / Chair Notes">
            <div className="summary-list">
              <div className="summary-row">
                <span>Advisor</span>
                <span>{data.advisor || '—'}</span>
              </div>
              <div className="summary-row">
                <span>Advisor Notes</span>
                <span>{data.advisor_notes || '—'}</span>
              </div>
              <div className="summary-row">
                <span>Chair Notes</span>
                <span>{data.chair_notes || '—'}</span>
              </div>
            </div>
          </Card>
        </div>
      )}

      {loading || !data ? null : (
        <Card title={isChair ? 'Chair Decision' : 'Advisor Decision'}>
          {!canReview ? (
            <p className="subtle">You have view-only access to requests.</p>
          ) : canAct ? (
            <>
              <label className="input-field">
                <span className="input-label">Notes</span>
                <textarea
                  className="input"
                  rows="4"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Add a note for the student"
                />
              </label>
              <div className="form-actions">
                <Button variant="secondary" type="button" disabled={saving} onClick={() => handleDecision('decline', isChair)}>
                  Decline
                </Button>
                <Button type="button" disabled={saving} onClick={() => handleDecision('approve', isChair)}>
                  Approve
                </Button>
              </div>
            </>
          ) : (
            <p className="subtle">Decision is locked. This request has already been processed.</p>
          )}
        </Card>
      )}
    </div>
  );
}

function renderStatus(req) {
  if (req.status === 1 && req.advisor_decision === 0 && req.chair_decision === 0) {
    return <Badge tone="warning">Pending</Badge>;
  }
  if (req.status === 1 && req.advisor_decision === 1 && req.chair_decision === 0) {
    return <Badge tone="info">Chair Review</Badge>;
  }
  if (req.status === 0 && req.chair_decision === 1) {
    return <Badge tone="success">Approved</Badge>;
  }
  if (req.status === 0 && req.advisor_decision === 0) {
    return <Badge tone="error">Declined</Badge>;
  }
  return <Badge tone="info">Closed</Badge>;
}
